import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { ProductKey } from '@/components/signup/utils/products';
import { getDeferSiteNamingConfirmationPageUrl } from '@/components/signup/utils/url';
import { useUserContext } from '@/components/user-context/user-context-provider';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';

// The experiment targets Jira Software only — matches the `useLoggedOutJira`
// cohort in the experiment manifest. If we ever expand to JSM / JPD, add
// their product keys here and update the experiment's cohort accordingly.
const JIRA_PRODUCT_KEYS: ReadonlyArray<string> = [ProductKey.JIRA_SOFTWARE];

/**
 * Returns a `getContinueUrl` function suitable for passing to <AccountCreation>
 * for Jira signups that may be enrolled in the `d1d6-defer-site-naming` experiment.
 *
 * - In the variant, returns the confirmation page URL with `deferSiteNaming=true`,
 *   bypassing the site naming step.
 * - In control (or for non-Jira products), returns `undefined` so AccountCreation
 *   falls back to its default `continueUrl` (the site naming page).
 *
 * Call sites that may render for non-Jira products (e.g. the generic UniformSignUpForm)
 * are guarded by a product-key check inside this hook, so the experiment cannot be
 * accidentally evaluated for the wrong product.
 */
export const useDeferSiteNamingContinueUrl = (): (() => string | undefined) => {
	const { product, edition } = useUserContext();
	const { locale } = useIntl();
	const deferSiteNamingExperiment = useExperiment(experiments.d1d6DeferSiteNaming);

	const isEnrolledInVariant =
		deferSiteNamingExperiment.type === 'enrolled' && deferSiteNamingExperiment.value;

	return useCallback(() => {
		// Bail early for control, non-enrolled, missing product, or non-Jira products.
		// Keeping `product` in the predicate gives TS the narrowing it needs below.
		if (!isEnrolledInVariant || !product || !JIRA_PRODUCT_KEYS.includes(product.key)) {
			return undefined;
		}
		return getDeferSiteNamingConfirmationPageUrl({
			productKey: product.key,
			edition: edition ?? 'free',
			locale,
		});
	}, [product, edition, isEnrolledInVariant, locale]);
};
